import { useState, useEffect, useRef, useCallback } from 'react';
import { WS_BASE_URL } from '../api/config';
import { agentApi } from '../services/api';
import {
    attachSubAgentEvent,
    flushPendingSubAgentEvents,
    isSubAgentEvent,
    markSubAgentEventsStopped,
} from '../utils/subAgentEvents';
import {
    applyToolResultToMessages,
    bufferPendingToolResult,
    clearPendingToolResults,
    flushPendingToolResults,
    hasPendingToolCallMessages,
    mergePersistedToolResults,
} from '../utils/toolCallEvents';

/**
 * WebSocket states
 */
const ConnectionState = {
    CONNECTING: 'CONNECTING',
    CONNECTED: 'CONNECTED',
    DISCONNECTED: 'DISCONNECTED',
    ERROR: 'ERROR',
};

const SESSION_HISTORY_PAGE_SIZE = 50;
const SESSION_HISTORY_REQUEST = {
    messageLimit: SESSION_HISTORY_PAGE_SIZE,
    includeExecutions: false,
    includeSystemSnapshot: false,
    compactToolOutputs: true,
};

const STOP_PENDING_ACTIVITY_TYPES = new Set([
    'skills_loaded',
    'tools_loaded',
    'iteration_started',
    'llm_thinking',
    'assistant_status',
    'assistant_chunk',
    'llm_response',
    'assistant_message',
    'message_received',
    'tool_call',
    'tool_result',
    'execution_data',
    'validator_result',
]);

const STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES = new Set([
    'execution_started',
    'assistant_status',
    'tool_call',
    'llm_response',
]);

function shouldIgnoreDuringStop(data) {
    if (!data) return false;
    if (isSubAgentEvent(data)) {
        return STOP_PENDING_SUB_AGENT_ACTIVITY_TYPES.has(data.type);
    }
    return STOP_PENDING_ACTIVITY_TYPES.has(data.type);
}

function createStoppedToolResult() {
    return {
        success: false,
        data: { cancelled: true },
        error: 'Execution cancelled by user.',
    };
}

function markPendingToolCallsStopped(messages) {
    if (!Array.isArray(messages) || messages.length === 0) return messages;

    let changed = false;
    const next = messages.map((message) => {
        let updated = message;

        if (Array.isArray(message.subAgentEvents) && message.subAgentEvents.length > 0) {
            const nested = markSubAgentEventsStopped(message.subAgentEvents);
            if (nested !== message.subAgentEvents) {
                updated = { ...updated, subAgentEvents: nested };
                changed = true;
            }
        }

        if (
            (message.role === 'tool_call' || message.role === 'TOOL_CALL') &&
            (message.result === undefined || message.result === null)
        ) {
            updated = {
                ...updated,
                result: createStoppedToolResult(),
                success: false,
            };
            changed = true;
        }

        return updated;
    });

    return changed ? next : messages;
}

function getPersistedMessageIdFromEvent(data) {
    const rawId = data?.message_id ?? data?.messageId ?? data?.assistant_message_id ?? data?.id;
    const numericId = Number(rawId);
    return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function attachUserMessageId(messages, data) {
    const numericId = Number(data?.user_message_id ?? data?.userMessageId);
    if (!Number.isInteger(numericId) || numericId <= 0) return messages;
    if (messages.some((message) => Number(message.dbMessageId ?? message.id) === numericId)) {
        return messages;
    }

    const targetIndex = [...messages]
        .reverse()
        .findIndex((message) => message.role === 'user' && !getPersistedMessageIdFromEvent(message));
    if (targetIndex < 0) return messages;

    const actualIndex = messages.length - 1 - targetIndex;
    return messages.map((message, index) => (
        index === actualIndex
            ? { ...message, dbMessageId: numericId }
            : message
    ));
}

function upsertAssistantMessage(messages, data, { completed = false } = {}) {
    const executionId = data.execution_id || data.executionId;
    const content = data.content || '';
    const dbMessageId = getPersistedMessageIdFromEvent(data);
    const id = dbMessageId ? String(dbMessageId) : executionId || `assistant-${Date.now()}`;
    const timestamp = new Date().toISOString();
    const existingIndex = executionId
        ? messages.findIndex((message) => (
            message.role === 'assistant' && message.executionId === executionId
        ))
        : -1;

    const nextMessage = {
        id,
        role: 'assistant',
        content,
        dbMessageId,
        executionId,
        completed,
        timestamp,
    };

    if (existingIndex < 0) {
        return [...messages, nextMessage];
    }

    return messages.map((message, index) => {
        if (index !== existingIndex) return message;
        return {
            ...message,
            id: message.id || id,
            dbMessageId: message.dbMessageId || dbMessageId || undefined,
            content: content || message.content,
            executionId: message.executionId || executionId,
            completed: message.completed || completed,
            timestamp: message.timestamp || timestamp,
        };
    });
}

function mapPersistedSessionMessage(msg) {
    if (msg.role === 'tool_call') {
        return {
            id: msg.id,
            dbMessageId: msg.id,
            role: 'tool_call',
            toolName: msg.tool_name,
            toolArgs: msg.tool_input,
            result: msg.tool_output,
            success: msg.success,
            timestamp: msg.created_at,
            toolCallId: msg.tool_call_id || msg.id,
            toolRationale: (msg.tool_rationale || msg.toolRationale || '').trim() || undefined,
            toolGroupRationale: (msg.tool_group_rationale || msg.toolGroupRationale || '').trim() || undefined,
            subAgentEvents: Array.isArray(msg.sub_agent_events)
                ? msg.sub_agent_events
                : [],
        };
    }

    if (msg.role === 'assistant_status') {
        return null;
    }

    return {
        id: msg.id,
        dbMessageId: msg.id,
        role: msg.role?.toLowerCase() === 'user' ? 'user' : 'assistant',
        content: msg.content,
        executionId: msg.execution,
        timestamp: msg.created_at,
    };
}

function mapPersistedSessionMessages(messages = []) {
    return messages.map(mapPersistedSessionMessage).filter(Boolean);
}

/**
 * Custom hook for Agent WebSocket communication.
 * 
 * Handles:
 * - WebSocket connection lifecycle
 * - Message sending/receiving
 * - Session management
 * - Automatic reconnection
 * - Streaming responses
 * 
 * @param {number|string|null} agentId - Optional agent ID. If null, connects to Main Assistant.
 * @returns {Object} Hook state and methods
 */
const useMainAssistantSocket = (agentId = null) => {
    const [connectionState, setConnectionState] = useState(ConnectionState.DISCONNECTED);
    const [agentInfo, setAgentInfo] = useState(null);
    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [currentSessionData, setCurrentSessionData] = useState(null);
    const [liveUsage, setLiveUsage] = useState({
        executionId: null,
        sessionId: null,
        totalTokens: 0,
        totalCost: 0,
    });
    const [messages, setMessages] = useState([]);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [nextBeforeMessageId, setNextBeforeMessageId] = useState(null);
    const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [isLoadingSession, setIsLoadingSession] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [error, setError] = useState(null);
    const [currentExecutionId, setCurrentExecutionId] = useState(null);
    const [pendingConfirmation, setPendingConfirmation] = useState(null);

    const socketRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectAttemptsRef = useRef(0);
    const maxReconnectAttempts = 5;
    const messageQueueRef = useRef([]);
    const onSessionTitleUpdateRef = useRef(null);
    const onSessionCreatedRef = useRef(null);
    const currentAgentIdRef = useRef(agentId);
    const currentSessionIdRef = useRef(null);
    const currentExecutionIdRef = useRef(null);
    const sessionLoadRequestRef = useRef(0);
    const isSwitchingRef = useRef(false);
    const pendingSessionRefreshRef = useRef(false);
    const shouldRecoverOnOpenRef = useRef(false);
    const stopPendingRef = useRef(false);
    const messagesRef = useRef([]);
    // Buffer for nested sub-agent events that arrive before their parent
    // tool_call message is committed to React state. Flushed each time a
    // tool_call message is appended.
    const pendingSubAgentEventsRef = useRef({});
    const pendingToolResultsRef = useRef({});
    const pendingToolRationaleRef = useRef(null);

    const resetLiveUsage = useCallback((sessionId = null) => {
        setLiveUsage({
            executionId: null,
            sessionId,
            totalTokens: 0,
            totalCost: 0,
        });
    }, []);

    const refreshSessionData = useCallback((sessionId) => {
        if (!sessionId) {
            setCurrentSessionData(null);
            return;
        }

        agentApi.getSession(sessionId, {
            includeMessages: false,
            includeExecutions: false,
            includeSystemSnapshot: false,
        })
            .then((session) => {
                if (!currentSessionIdRef.current || currentSessionIdRef.current === sessionId) {
                    setCurrentSessionData(session);
                }
            })
            .catch((err) => {
                console.warn('[MainAssistantSocket] Failed to refresh session details:', err);
            });
    }, []);

    const updateLiveUsageFromEvent = useCallback((data) => {
        const eventSessionId = data.session_id || currentSessionIdRef.current || null;
        setLiveUsage((prev) => ({
            executionId: data.execution_id || prev.executionId || null,
            sessionId: eventSessionId || prev.sessionId || null,
            totalTokens: Number(data.total_tokens ?? prev.totalTokens ?? 0) || 0,
            totalCost: Number(data.total_cost ?? prev.totalCost ?? 0) || 0,
        }));
    }, []);

    const reconcileExecutionToolMessages = useCallback(async (executionId) => {
        if (!executionId) return;
        try {
            const persistedMessages = await agentApi.getExecutionMessages(executionId);
            setMessages((prev) => mergePersistedToolResults(prev, persistedMessages));
        } catch (err) {
            console.warn('[MainAssistantSocket] Failed to reconcile persisted tool messages:', err);
        }
    }, []);

    const syncActiveSessionOverSocket = useCallback((sessionId) => {
        if (!sessionId || socketRef.current?.readyState !== WebSocket.OPEN) return;
        socketRef.current.send(JSON.stringify({
            type: 'load_session',
            session_id: sessionId,
            include_messages: false,
        }));
    }, []);

    const applySessionHistory = useCallback((session, { prepend = false } = {}) => {
        const loadedMessages = mapPersistedSessionMessages(session?.messages || []);
        const page = session?.messages_page || {};

        clearPendingToolResults(pendingToolResultsRef.current);
        pendingToolRationaleRef.current = null;
        pendingSubAgentEventsRef.current = {};

        if (prepend) {
            setMessages((prev) => [...loadedMessages, ...prev]);
        } else {
            setMessages(loadedMessages);
        }

        setCurrentSessionData(session || null);
        setHasMoreMessages(Boolean(page.has_more));
        setNextBeforeMessageId(page.next_before_message_id ?? null);
    }, []);

    const hydrateSessionFromApi = useCallback(async (
        sessionId,
        { beforeMessageId = null, prepend = false, syncSocket = false } = {},
    ) => {
        if (!sessionId) return false;

        const requestId = prepend ? sessionLoadRequestRef.current : ++sessionLoadRequestRef.current;
        if (prepend) {
            setIsLoadingMoreMessages(true);
        } else {
            setIsLoadingSession(true);
        }
        setError(null);

        try {
            const session = await agentApi.getSession(sessionId, {
                ...SESSION_HISTORY_REQUEST,
                beforeMessageId,
            });
            if (!prepend && requestId !== sessionLoadRequestRef.current) {
                return false;
            }
            if (currentSessionIdRef.current && currentSessionIdRef.current !== sessionId) {
                return false;
            }

            applySessionHistory(session, { prepend });
            if (syncSocket) {
                syncActiveSessionOverSocket(sessionId);
            }
            return true;
        } catch (err) {
            console.warn('[MainAssistantSocket] Failed to load session history:', err);
            const message = err?.response?.data?.error
                || err?.response?.data?.detail
                || err?.message
                || 'Failed to load conversation.';
            if (!prepend) {
                setMessages([]);
                setCurrentSessionData(null);
                setHasMoreMessages(false);
                setNextBeforeMessageId(null);
            }
            setError(message);
            return false;
        } finally {
            if (prepend) {
                setIsLoadingMoreMessages(false);
            } else if (requestId === sessionLoadRequestRef.current) {
                setIsLoadingSession(false);
            }
        }
    }, [applySessionHistory, syncActiveSessionOverSocket]);

    /**
     * Get WebSocket URL with auth token
     */
    const getWebSocketUrl = useCallback((targetAgentId) => {
        const token = localStorage.getItem('token');
        if (!token) {
            console.error('[AgentSocket] No auth token found');
            return null;
        }

        const baseUrl = WS_BASE_URL || (() => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${protocol}//${window.location.host}`;
        })();

        // If agentId is provided, connect to agent-specific WebSocket
        // Otherwise, connect to Main Assistant
        if (targetAgentId) {
            return `${baseUrl}/ws/agents/${targetAgentId}/chat/?token=${encodeURIComponent(token)}`;
        }
        return `${baseUrl}/ws/main-assistant/chat/?token=${encodeURIComponent(token)}`;
    }, []);

    /**
     * Connect to WebSocket
     */
    const connect = useCallback((targetAgentId = null) => {
        // Use provided agentId or fall back to the one passed to the hook
        const effectiveAgentId = targetAgentId !== undefined ? targetAgentId : currentAgentIdRef.current;

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log('[AgentSocket] Already connected');
            return;
        }

        const url = getWebSocketUrl(effectiveAgentId);
        if (!url) {
            setError('No authentication token');
            setConnectionState(ConnectionState.ERROR);
            return;
        }

        console.log(`[AgentSocket] Connecting to ${effectiveAgentId ? `agent ${effectiveAgentId}` : 'Main Assistant'}...`);
        setConnectionState(ConnectionState.CONNECTING);
        setError(null);
        currentAgentIdRef.current = effectiveAgentId;

        try {
            socketRef.current = new WebSocket(url);

            socketRef.current.onopen = () => {
                console.log(`[AgentSocket] Connected to ${effectiveAgentId ? `agent ${effectiveAgentId}` : 'Main Assistant'}`);
                setConnectionState(ConnectionState.CONNECTED);
                const wasReconnect = reconnectAttemptsRef.current > 0;
                const shouldRecoverOnOpen = wasReconnect || shouldRecoverOnOpenRef.current;
                shouldRecoverOnOpenRef.current = false;
                reconnectAttemptsRef.current = 0;

                // Process queued messages
                while (messageQueueRef.current.length > 0) {
                    const msg = messageQueueRef.current.shift();
                    socketRef.current.send(JSON.stringify(msg));
                }

                if (shouldRecoverOnOpen) {
                    const sessionId = currentSessionIdRef.current;
                    const executionId = currentExecutionIdRef.current;

                    if (sessionId) {
                        void hydrateSessionFromApi(sessionId, { syncSocket: true });
                    }

                    if (executionId) {
                        agentApi.getExecutionSummary(executionId)
                            .then((execution) => {
                                const status = String(execution.status || '').toUpperCase();
                                void reconcileExecutionToolMessages(executionId);
                                if (['COMPLETED', 'ERROR', 'CANCELLED', 'FAILED', 'STOPPED'].includes(status)) {
                                    setIsTyping(false);
                                    setStreamingContent('');
                                    setPendingConfirmation(null);
                                    setCurrentExecutionId(null);
                                    refreshSessionData(execution.session_id || sessionId);
                                    return;
                                }
                                setIsTyping(true);
                                if (socketRef.current?.readyState === WebSocket.OPEN) {
                                    socketRef.current.send(JSON.stringify({
                                        type: 'subscribe',
                                        execution_id: executionId,
                                    }));
                                }
                            })
                            .catch((err) => {
                                console.warn('[MainAssistantSocket] Failed to recover execution after reconnect:', err);
                            });
                    }
                }
            };

            socketRef.current.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    handleMessage(data);
                } catch (e) {
                    console.error('[AgentSocket] Failed to parse message:', e);
                }
            };

            socketRef.current.onclose = (event) => {
                console.log('[AgentSocket] Disconnected:', event.code, event.reason);
                setConnectionState(ConnectionState.DISCONNECTED);
                const shouldKeepRecovering = Boolean(
                    currentSessionIdRef.current ||
                    currentExecutionIdRef.current
                );
                shouldRecoverOnOpenRef.current = shouldKeepRecovering;

                // Server redeploying — reconnect immediately, skip backoff
                if (event.code === 4000) {
                    console.log('[AgentSocket] Server redeploying, reconnecting immediately...');
                    reconnectAttemptsRef.current = 0;
                    reconnectTimeoutRef.current = setTimeout(() => {
                        connect(currentAgentIdRef.current);
                    }, 100);
                    return;
                }

                // Attempt reconnection if not a normal close
                if (event.code !== 1000 && (reconnectAttemptsRef.current < maxReconnectAttempts || shouldKeepRecovering)) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                    console.log(`[AgentSocket] Reconnecting in ${delay}ms...`);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current++;
                        connect(currentAgentIdRef.current);
                    }, delay);
                }
            };

            socketRef.current.onerror = (event) => {
                console.error('[AgentSocket] Error:', event);
                setError('WebSocket connection error');
                setConnectionState(ConnectionState.ERROR);
            };

        } catch (e) {
            console.error('[AgentSocket] Failed to connect:', e);
            setError(e.message);
            setConnectionState(ConnectionState.ERROR);
        }
    }, [getWebSocketUrl, hydrateSessionFromApi, reconcileExecutionToolMessages, refreshSessionData]);

    /**
     * Disconnect from WebSocket
     */
    const disconnect = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }

        if (socketRef.current) {
            socketRef.current.close(1000, 'User disconnected');
            socketRef.current = null;
        }

        setConnectionState(ConnectionState.DISCONNECTED);
    }, []);

    /**
     * Switch to a different agent (disconnect and reconnect)
     */
    const switchAgent = useCallback((newAgentId) => {
        console.log(`[AgentSocket] Switching to ${newAgentId ? `agent ${newAgentId}` : 'Main Assistant'}`);
        // Set flag to prevent useEffect from reconnecting during switch
        isSwitchingRef.current = true;
        // Update the agent ID ref immediately
        currentAgentIdRef.current = newAgentId;
        // Clear messages when switching agents
        setMessages([]);
        setHasMoreMessages(false);
        setNextBeforeMessageId(null);
        setCurrentSessionId(null);
        setCurrentSessionData(null);
        resetLiveUsage(null);
        currentSessionIdRef.current = null;
        pendingSessionRefreshRef.current = false;
        stopPendingRef.current = false;
        pendingSubAgentEventsRef.current = {};
        clearPendingToolResults(pendingToolResultsRef.current);
        pendingToolRationaleRef.current = null;
        setStreamingContent('');
        setIsTyping(false);

        // Disconnect and reconnect
        disconnect();
        setTimeout(() => {
            connect(newAgentId);
            // Clear the switching flag after connection is initiated
            setTimeout(() => {
                isSwitchingRef.current = false;
            }, 100);
        }, 100);
    }, [disconnect, connect, resetLiveUsage]);

    /**
     * Handle incoming WebSocket messages
     */
    const handleMessage = useCallback((data) => {
        const { type } = data;

        // Debug: log all incoming WebSocket messages
        if (type !== 'pong' && type !== 'assistant_chunk') {
            console.log('[MainAssistantSocket] Message received:', type, data);
        }

        if (stopPendingRef.current && shouldIgnoreDuringStop(data)) {
            console.log('[MainAssistantSocket] Ignoring late activity after stop request:', data.type);
            return;
        }

        // Nested sub-agent events are attached to the matching parent tool_call
        // message instead of being rendered as new top-level entries.
        if (isSubAgentEvent(data)) {
            console.log(
                '[MainAssistantSocket] SUB-AGENT event →', data.type,
                'parent:', data.parent_tool_call_id,
                'sub:', data.sub_agent_name,
            );
            setMessages(prev => {
                const updated = attachSubAgentEvent(prev, data);
                if (updated === prev) {
                    // Parent not yet in state - buffer the event for later flush.
                    const buf = pendingSubAgentEventsRef.current;
                    if (!buf[data.parent_tool_call_id]) buf[data.parent_tool_call_id] = [];
                    buf[data.parent_tool_call_id].push(data);
                    console.warn(
                        '[MainAssistantSocket] Buffered sub-agent event (parent not committed yet)',
                        { parent: data.parent_tool_call_id, type: data.type, queued: buf[data.parent_tool_call_id].length },
                    );
                    return prev;
                }
                return flushPendingSubAgentEvents(updated, pendingSubAgentEventsRef.current);
            });
            return;
        }

        switch (type) {
            case 'connected':
                setAgentInfo({
                    id: data.agent_id,
                    name: data.agent_name,
                    model: data.model || null,
                    tts_model: data.tts_model || null,
                });
                if (data.session_id) {
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    resetLiveUsage(data.session_id);
                    console.log('[AgentSocket] Loading existing session messages:', data.session_id);
                    void hydrateSessionFromApi(data.session_id, { syncSocket: true });
                }
                break;

            case 'session_started':
                // session_started is sent on every message execution
                // Check if this is actually a NEW session (different from current OR first session for this agent)
                if (data.session_id) {
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    const wasFirstSession = currentSessionIdRef.current === null;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    setCurrentSessionData((prev) => ({
                        ...(prev?.id === data.session_id ? prev : {}),
                        id: data.session_id,
                        workspace: data.workspace_id
                            || (prev?.id === data.session_id ? prev.workspace : null),
                        workspace_task: data.workspace_task_id
                            || (prev?.id === data.session_id ? prev.workspace_task : null),
                    }));
                    // Mark that we need to refresh sessions after execution completes
                    if (isNewSession || wasFirstSession) {
                        console.log('[AgentSocket] New session detected via session_started:', data.session_id, { isNewSession, wasFirstSession });
                        pendingSessionRefreshRef.current = true;
                    }
                }
                break;

            case 'session_created':
                // session_created is only sent when a NEW session is explicitly created via "New Chat"
                console.log('[AgentSocket] session_created', data);
                if (data.session_id) {
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    setCurrentSessionData({
                        id: data.session_id,
                        workspace: data.workspace_id || null,
                        workspace_task: data.workspace_task_id || null,
                        title: data.title || '',
                        messages: [],
                        message_count: 0,
                        executions: [],
                        execution_count: 0,
                    });
                    resetLiveUsage(data.session_id);
                    // Clear messages for new session
                    pendingSubAgentEventsRef.current = {};
                    clearPendingToolResults(pendingToolResultsRef.current);
                    pendingToolRationaleRef.current = null;
                    setMessages([]);
                    setHasMoreMessages(false);
                    setNextBeforeMessageId(null);
                    // Notify that a new session was created - refresh session list
                    if (onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Triggering onSessionCreated callback');
                        onSessionCreatedRef.current(data.session_id, data.title || null);
                    }
                }
                break;

            case 'execution_started':
                stopPendingRef.current = false;
                setIsTyping(true);
                setStreamingContent('');
                if (data.execution_id) {
                    setCurrentExecutionId(data.execution_id);
                }
                if (data.session_id) {
                    // Check if this is a new session using ref to avoid stale closure
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    if (isNewSession && onSessionCreatedRef.current) {
                        onSessionCreatedRef.current(data.session_id, null);
                    }
                }
                setLiveUsage({
                    executionId: data.execution_id || null,
                    sessionId: data.session_id || currentSessionIdRef.current || null,
                    totalTokens: 0,
                    totalCost: 0,
                });
                break;

            case 'message_received':
                console.log('[AgentSocket] message_received', {
                    sessionId: data.session_id,
                    currentRef: currentSessionIdRef.current,
                    isNew: data.session_id !== currentSessionIdRef.current
                });
                setIsTyping(true);
                setStreamingContent('');
                if (data.session_id) {
                    // Check if this is a new session using ref to avoid stale closure
                    const isNewSession = data.session_id !== currentSessionIdRef.current;
                    currentSessionIdRef.current = data.session_id;
                    setCurrentSessionId(data.session_id);
                    if (isNewSession && onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Triggering onSessionCreated callback');
                        onSessionCreatedRef.current(data.session_id, data.session_title || null);
                    }
                }
                // Emit session title update event if title was set
                if (data.session_title && onSessionTitleUpdateRef.current) {
                    onSessionTitleUpdateRef.current(data.session_id, data.session_title);
                }
                break;

            case 'skills_loaded':
            case 'tools_loaded':
            case 'iteration_started':
            case 'llm_thinking':
                // Status updates - keep typing indicator active
                setIsTyping(true);
                break;

            case 'assistant_status':
                setIsTyping(true);
                pendingToolRationaleRef.current = (data.content || '').trim() || null;
                break;

            case 'assistant_chunk':
                // Streaming text chunk
                setStreamingContent(prev => prev + (data.content || ''));
                break;

            case 'llm_response':
                // Response from LLM (may be streaming chunks or final)
                if (data.is_final) {
                    stopPendingRef.current = false;
                    setIsTyping(false);
                    setStreamingContent('');

                    // Add/update the provisional assistant message. The later
                    // assistant_message event marks the same execution complete.
                    setMessages(prev => upsertAssistantMessage(prev, data));
                } else {
                    // Streaming chunk
                    setStreamingContent(prev => prev + (data.content || ''));
                }
                break;

            case 'assistant_message':
                // Final response (alternative format)
                stopPendingRef.current = false;
                setIsTyping(false);
                setStreamingContent('');
                setCurrentExecutionId(data.execution_id);

                // Add/update the completed assistant message without duplicating
                // the final llm_response entry for the same execution.
                setMessages(prev => upsertAssistantMessage(
                    attachUserMessageId(prev, data),
                    data,
                    { completed: true },
                ));
                break;

            case 'tool_call':
                // Tool is being called
                setMessages(prev => {
                    const toolRationale = (data.tool_rationale || data.toolRationale || '').trim() || undefined;
                    const toolGroupRationale = (data.tool_group_rationale || data.toolGroupRationale || '').trim()
                        || pendingToolRationaleRef.current
                        || undefined;
                    pendingToolRationaleRef.current = null;
                    const next = [...prev, {
                        id: `tool-${Date.now()}`,
                        role: 'tool_call',
                        toolName: data.tool_name,
                        toolArgs: data.tool_args || data.arguments,
                        arguments: data.arguments,
                        toolCallId: data.tool_call_id, // needed to attach sub-agent events
                        toolRationale,
                        toolGroupRationale,
                        subAgentEvents: [],
                        timestamp: new Date().toISOString(),
                    }];
                    // Flush any nested sub-agent events that arrived before
                    // this parent tool_call committed to state.
                    const withSubAgents = flushPendingSubAgentEvents(next, pendingSubAgentEventsRef.current);
                    return flushPendingToolResults(withSubAgents, pendingToolResultsRef.current);
                });
                break;

            case 'tool_result':
                // Tool execution result. Prefer matching by tool_call_id when
                // present so nested events stay associated with their parent.
                setMessages(prev => {
                    const applied = applyToolResultToMessages(prev, {
                        toolName: data.tool_name,
                        toolCallId: data.tool_call_id,
                        result: data.result,
                    });
                    if (!applied.applied) {
                        bufferPendingToolResult(pendingToolResultsRef.current, {
                            toolName: data.tool_name,
                            toolCallId: data.tool_call_id,
                            result: data.result,
                        });
                        return prev;
                    }
                    return applied.messages;
                });
                break;

            case 'validator_result': {
                const runs = Array.isArray(data.runs) ? data.runs : [];
                const names = runs
                    .map((run) => run.policy_name)
                    .filter(Boolean)
                    .join(', ');
                const status = data.allowed ? 'passed' : 'actioned';
                const message = data.message || `Validator ${status}`;
                setMessages(prev => [...prev, {
                    id: `validator-${Date.now()}`,
                    role: 'validator',
                    content: names
                        ? `Validator ${status}: ${names}\n${message}`
                        : `Validator ${status}\n${message}`,
                    timestamp: new Date().toISOString(),
                    validation: data,
                }]);
                break;
            }

            case 'session_loaded':
                currentSessionIdRef.current = data.session_id;
                setCurrentSessionId(data.session_id);
                if (data.history_loaded === false) {
                    setCurrentSessionData((prev) => ({
                        ...(prev || {}),
                        id: data.session_id,
                        workspace: data.workspace_id || prev?.workspace || null,
                        workspace_task: data.workspace_task_id || prev?.workspace_task || null,
                        title: data.title || prev?.title || '',
                    }));
                    setIsLoadingSession(false);
                    break;
                }
                applySessionHistory({
                    id: data.session_id,
                    workspace: data.workspace_id || null,
                    workspace_task: data.workspace_task_id || null,
                    title: data.title || '',
                    messages: data.messages || [],
                    messages_page: data.messages_page || null,
                    message_count: data.message_count ?? data.messages?.length ?? 0,
                });
                resetLiveUsage(data.session_id);
                setIsLoadingSession(false);
                break;

            case 'session_not_found':
                // Requested session doesn't exist anymore
                console.warn('[AgentSocket] Session not found:', data.requested_session_id);
                setIsLoadingSession(false);
                if (data.fallback_session_id) {
                    // Backend found a fallback session - use it
                    console.log('[AgentSocket] Using fallback session:', data.fallback_session_id);
                    currentSessionIdRef.current = data.fallback_session_id;
                    setCurrentSessionId(data.fallback_session_id);
                    if (data.history_loaded === false) {
                        setCurrentSessionData({
                            id: data.fallback_session_id,
                            title: data.fallback_title || '',
                        });
                    } else {
                        applySessionHistory({
                            id: data.fallback_session_id,
                            title: data.fallback_title || '',
                            messages: data.messages || [],
                            messages_page: data.messages_page || null,
                            message_count: data.message_count ?? data.messages?.length ?? 0,
                        });
                    }
                    resetLiveUsage(data.fallback_session_id);
                } else {
                    // No fallback session - clear everything and let user start fresh
                    console.log('[AgentSocket] No fallback session - starting fresh');
                    currentSessionIdRef.current = null;
                    setCurrentSessionId(null);
                    pendingSubAgentEventsRef.current = {};
                    clearPendingToolResults(pendingToolResultsRef.current);
                    pendingToolRationaleRef.current = null;
                    setMessages([]);
                    setCurrentSessionData(null);
                    setHasMoreMessages(false);
                    setNextBeforeMessageId(null);
                    resetLiveUsage(null);
                }
                // Don't refresh session list - it causes UI flashing
                break;

            case 'pending_confirmation':
                // A dangerous tool needs user confirmation
                console.log('[MainAssistantSocket] 🔔 PENDING CONFIRMATION received:', data);
                setPendingConfirmation({
                    executionId: data.execution_id,
                    toolName: data.tool_name,
                    toolArgs: data.tool_args,
                    toolCallId: data.tool_call_id,
                    dangerLevel: data.danger_level || 0,
                    message: data.message,
                });
                break;

            case 'confirmation_resolved':
                // Backend confirmed/denied the tool — clear the prompt
                setPendingConfirmation(null);
                break;

            case 'tool_confirmation_received':
                // Acknowledgement from the consumer
                break;

            case 'stop_requested':
                stopPendingRef.current = true;
                setIsTyping(false);
                setStreamingContent('');
                setPendingConfirmation(null);
                break;

            case 'execution_stopped':
                stopPendingRef.current = false;
                setIsTyping(false);
                setStreamingContent('');
                setPendingConfirmation(null);
                setCurrentExecutionId(null);
                setMessages((prev) => markPendingToolCallsStopped(prev));
                break;

            case 'execution_error':
                {
                    const message = data.error || 'Execution error';
                    stopPendingRef.current = false;
                    setIsTyping(false);
                    setStreamingContent('');
                    setPendingConfirmation(null);
                    setCurrentExecutionId(null);
                    setError(message);
                    setMessages((prev) => [
                        ...markPendingToolCallsStopped(prev),
                        {
                            id: `execution-error-${Date.now()}`,
                            role: 'validator',
                            content: `Execution failed\n${message}`,
                            timestamp: new Date().toISOString(),
                            validation: {
                                allowed: false,
                                terminal: true,
                                error: message,
                            },
                        },
                    ]);
                }
                break;

            case 'execution_completed':
            case 'execution_data':
                updateLiveUsageFromEvent(data);
                // Execution finished - if this was a new session, NOW is the time to refresh
                // because the session is definitely saved in the database
                if (data.type === 'execution_completed') {
                    if (stopPendingRef.current && data.status !== 'CANCELLED') {
                        stopPendingRef.current = false;
                        setIsTyping(false);
                        setStreamingContent('');
                        setPendingConfirmation(null);
                        setCurrentExecutionId(null);
                        setMessages((prev) => markPendingToolCallsStopped(prev));
                        break;
                    }
                    stopPendingRef.current = false;
                    setIsTyping(false);
                    setPendingConfirmation(null);
                    if (hasPendingToolCallMessages(messagesRef.current)) {
                        reconcileExecutionToolMessages(data.execution_id || currentExecutionIdRef.current);
                    }
                    setCurrentExecutionId(null);
                    refreshSessionData(data.session_id || currentSessionIdRef.current);
                    if (
                        hasPendingToolCallMessages(messagesRef.current) &&
                        currentSessionIdRef.current
                    ) {
                        void hydrateSessionFromApi(currentSessionIdRef.current, { syncSocket: true });
                    }
                    // Trigger session refresh if we had a pending new session
                    if (pendingSessionRefreshRef.current && onSessionCreatedRef.current) {
                        console.log('[AgentSocket] Execution completed, refreshing sessions for new session');
                        pendingSessionRefreshRef.current = false;
                        onSessionCreatedRef.current(currentSessionIdRef.current, null);
                    }
                }
                break;

            case 'error':
                setError(data.error);
                setIsTyping(false);
                break;

            case 'pong':
                // Heartbeat response
                break;

            default:
                console.log('[MainAssistantSocket] Unknown message type:', type, data);
        }
    }, [resetLiveUsage]);

    /**
     * Send a message to the assistant
     */
    const sendMessage = useCallback((content, sessionId = null, options = {}) => {
        if (!content?.trim()) return false;

        const message = {
            type: 'message',
            content: content.trim(),
            session_id: sessionId || currentSessionId,
        };

        if (options.attachment_ids && options.attachment_ids.length > 0) {
            message.attachment_ids = options.attachment_ids;
        }

        // Add user message to history immediately
        setMessages(prev => [...prev, {
            id: `user-${Date.now()}`,
            role: 'user',
            content: content.trim(),
            timestamp: new Date().toISOString(),
        }]);

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            return true;
        } else {
            // Queue message for when connection is established
            messageQueueRef.current.push(message);
            connect();
            return false;
        }
    }, [currentSessionId, connect]);

    /**
     * Create a new chat session
     */
    const newSession = useCallback((title = '') => {
        console.log('[AgentSocket] newSession called, sending new_session message');
        const message = {
            type: 'new_session',
            title,
        };

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            console.log('[AgentSocket] Sending new_session:', message);
            socketRef.current.send(JSON.stringify(message));
            // Clear messages for new session
            pendingSubAgentEventsRef.current = {};
            clearPendingToolResults(pendingToolResultsRef.current);
            pendingToolRationaleRef.current = null;
            setMessages([]);
            setHasMoreMessages(false);
            setNextBeforeMessageId(null);
            setCurrentSessionData(null);
            resetLiveUsage(null);
            return true;
        }
        console.log('[AgentSocket] newSession - WebSocket not open');
        return false;
    }, [resetLiveUsage]);

    /**
     * Load an existing session
     */
    const loadSession = useCallback((sessionId) => {
        if (!sessionId) return false;

        currentSessionIdRef.current = sessionId;
        setCurrentSessionId(sessionId);
        setCurrentSessionData(null);
        resetLiveUsage(sessionId);
        pendingSubAgentEventsRef.current = {};
        clearPendingToolResults(pendingToolResultsRef.current);
        pendingToolRationaleRef.current = null;
        setHasMoreMessages(false);
        setNextBeforeMessageId(null);
        setMessages([]); // Clear messages while loading

        void hydrateSessionFromApi(sessionId, { syncSocket: true });
        return true;
    }, [hydrateSessionFromApi, resetLiveUsage]);

    const loadOlderMessages = useCallback(() => {
        const sessionId = currentSessionIdRef.current;
        if (!sessionId || !nextBeforeMessageId || isLoadingMoreMessages) return false;
        void hydrateSessionFromApi(sessionId, {
            beforeMessageId: nextBeforeMessageId,
            prepend: true,
        });
        return true;
    }, [hydrateSessionFromApi, isLoadingMoreMessages, nextBeforeMessageId]);

    /**
     * Confirm or deny a dangerous tool execution
     */
    const confirmTool = useCallback((executionId, toolName, confirmed, toolCallId = null) => {
        const message = {
            type: 'confirm_tool',
            execution_id: executionId,
            tool_name: toolName,
            confirmed,
        };
        if (toolCallId) {
            message.tool_call_id = toolCallId;
        }

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            // Optimistically clear after a short delay (backend will also send confirmation_resolved)
            if (!confirmed) {
                setPendingConfirmation(null);
            }
            return true;
        }
        return false;
    }, []);

    /**
     * Stop the current execution
     */
    const stopExecution = useCallback(() => {
        const message = { type: 'stop' };

        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            stopPendingRef.current = true;
            setIsTyping(false);
            setStreamingContent('');
            setPendingConfirmation(null);
            return true;
        }
        return false;
    }, []);

    /**
     * Send a ping to keep connection alive
     */
    const ping = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: 'ping' }));
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            disconnect();
        };
    }, [disconnect]);

    // Heartbeat to keep connection alive
    useEffect(() => {
        if (connectionState !== ConnectionState.CONNECTED) return;

        const interval = setInterval(() => {
            ping();
        }, 30000);

        return () => clearInterval(interval);
    }, [connectionState, ping]);

    // Diagnostic: expose current messages tree on window so we can inspect
    // nested tool calls from the browser console.
    useEffect(() => {
        messagesRef.current = messages;
        if (typeof window !== 'undefined') {
            window.__debug_messages = messages;
            window.__debug_pending_sub_agent = pendingSubAgentEventsRef.current;
            window.__debug_pending_tool_results = pendingToolResultsRef.current;
            window.__debug_pending_tool_rationale = pendingToolRationaleRef.current;
        }
    }, [messages]);

    useEffect(() => {
        currentExecutionIdRef.current = currentExecutionId;
    }, [currentExecutionId]);

    useEffect(() => {
        if (!currentExecutionId || !hasPendingToolCallMessages(messages)) {
            return;
        }

        let cancelled = false;
        const refreshToolMessages = async () => {
            if (cancelled) return;
            await reconcileExecutionToolMessages(currentExecutionId);
        };

        refreshToolMessages();
        const interval = setInterval(refreshToolMessages, 1500);

        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [currentExecutionId, messages, reconcileExecutionToolMessages]);

    // Set callback for session title updates
    const setOnSessionTitleUpdate = useCallback((callback) => {
        onSessionTitleUpdateRef.current = callback;
    }, []);

    // Set callback for when a new session is created
    const setOnSessionCreated = useCallback((callback) => {
        onSessionCreatedRef.current = callback;
    }, []);

    return {
        // State
        connectionState,
        isConnected: connectionState === ConnectionState.CONNECTED,
        isConnecting: connectionState === ConnectionState.CONNECTING,
        isSwitching: isSwitchingRef.current,
        agentInfo,
        currentSessionId,
        currentSessionData,
        liveUsage,
        messages,
        hasMoreMessages,
        isLoadingMoreMessages,
        isTyping,
        isLoadingSession,
        streamingContent,
        error,
        currentExecutionId,
        pendingConfirmation,

        // Actions
        connect,
        disconnect,
        switchAgent,
        sendMessage,
        newSession,
        loadSession,
        loadOlderMessages,
        stopExecution,
        confirmTool,
        setOnSessionTitleUpdate,
        setOnSessionCreated,

        // Helpers
        clearMessages: () => {
            pendingSubAgentEventsRef.current = {};
            clearPendingToolResults(pendingToolResultsRef.current);
            pendingToolRationaleRef.current = null;
            setMessages([]);
            setHasMoreMessages(false);
            setNextBeforeMessageId(null);
        },
        clearError: () => setError(null),
        isSwitchingAgent: () => isSwitchingRef.current,
    };
};

export default useMainAssistantSocket;
export { ConnectionState };
